import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Layers, Target, ShoppingBag, Box, ListChecks, Mail, 
  ArrowRight, TrendingUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { adminGetStats, adminGetProducts } from '@/lib/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsData, productsData] = await Promise.all([
        adminGetStats(),
        adminGetProducts()
      ]);
      setStats(statsData);
      
      // Map top product IDs to product names
      if (statsData.top_product_ids && productsData) {
        const productMap = {};
        productsData.forEach(p => productMap[p.id] = p);
        const topProds = statsData.top_product_ids.map(([id, count]) => ({
          product: productMap[id],
          count
        })).filter(p => p.product);
        setTopProducts(topProds);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'Systems', value: stats?.systems || 0, icon: Layers, path: '/admin/systems', color: 'bg-blue-50 text-blue-600' },
    { label: 'Conditions', value: stats?.conditions || 0, icon: Target, path: '/admin/conditions', color: 'bg-green-50 text-green-600' },
    { label: 'Products', value: stats?.products || 0, icon: ShoppingBag, path: '/admin/products', color: 'bg-purple-50 text-purple-600' },
    { label: 'Protocols', value: stats?.protocols || 0, icon: Box, path: '/admin/protocols', color: 'bg-orange-50 text-orange-600' },
    { label: 'Quiz Questions', value: stats?.questions || 0, icon: ListChecks, path: '/admin/quiz-questions', color: 'bg-pink-50 text-pink-600' },
    { label: 'Email Captures', value: stats?.emails || 0, icon: Mail, path: '/admin/emails', color: 'bg-teal-50 text-teal-600' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-stone-400">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-serif text-2xl md:text-3xl text-[#2C2C2C] mb-2">Dashboard</h1>
        <p className="text-stone-600">Overview of your supplement finder data</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((stat) => (
          <Card 
            key={stat.label}
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate(stat.path)}
            data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
          >
            <CardContent className="p-4">
              <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center mb-3`}>
                <stat.icon className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <p className="text-2xl font-semibold text-[#2C2C2C]">{stat.value}</p>
              <p className="text-sm text-stone-500">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-serif">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {[
              { label: 'Add New Product', path: '/admin/products' },
              { label: 'Create Protocol', path: '/admin/protocols' },
              { label: 'Edit Quiz Questions', path: '/admin/quiz-questions' },
              { label: 'Import Data', path: '/admin/import' },
            ].map((action) => (
              <button
                key={action.label}
                onClick={() => navigate(action.path)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg hover:bg-stone-50 transition-colors text-left"
              >
                <span className="text-stone-700">{action.label}</span>
                <ArrowRight className="w-4 h-4 text-stone-400" strokeWidth={1.5} />
              </button>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-serif flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#4A6763]" strokeWidth={1.5} />
              Most Used Products
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topProducts.length > 0 ? (
              <div className="space-y-3">
                {topProducts.slice(0, 5).map((item, index) => (
                  <div 
                    key={item.product.id}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#4A6763]/10 text-[#4A6763] flex items-center justify-center text-xs font-medium">
                        {index + 1}
                      </span>
                      <span className="text-stone-700 text-sm">{item.product.product_name}</span>
                    </div>
                    <span className="text-xs text-stone-500 bg-stone-100 px-2 py-1 rounded-full">
                      {item.count} protocol{item.count !== 1 ? 's' : ''}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-stone-500 text-sm">No product usage data yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Setup Guide */}
      {(stats?.systems === 0 || stats?.conditions === 0 || stats?.products === 0) && (
        <Card className="border-[#C7A07A]/30 bg-[#C7A07A]/5">
          <CardHeader>
            <CardTitle className="text-lg font-serif text-[#C7A07A]">Getting Started</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-stone-600 mb-4">
              To set up your supplement finder, complete these steps:
            </p>
            <ol className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <span className={`w-5 h-5 rounded-full ${stats?.systems > 0 ? 'bg-green-500' : 'bg-stone-300'} text-white flex items-center justify-center text-xs`}>
                  {stats?.systems > 0 ? '✓' : '1'}
                </span>
                <span className={stats?.systems > 0 ? 'line-through text-stone-400' : ''}>Add body systems</span>
              </li>
              <li className="flex items-center gap-2">
                <span className={`w-5 h-5 rounded-full ${stats?.conditions > 0 ? 'bg-green-500' : 'bg-stone-300'} text-white flex items-center justify-center text-xs`}>
                  {stats?.conditions > 0 ? '✓' : '2'}
                </span>
                <span className={stats?.conditions > 0 ? 'line-through text-stone-400' : ''}>Add conditions</span>
              </li>
              <li className="flex items-center gap-2">
                <span className={`w-5 h-5 rounded-full ${stats?.products > 0 ? 'bg-green-500' : 'bg-stone-300'} text-white flex items-center justify-center text-xs`}>
                  {stats?.products > 0 ? '✓' : '3'}
                </span>
                <span className={stats?.products > 0 ? 'line-through text-stone-400' : ''}>Add products</span>
              </li>
              <li className="flex items-center gap-2">
                <span className={`w-5 h-5 rounded-full ${stats?.protocols > 0 ? 'bg-green-500' : 'bg-stone-300'} text-white flex items-center justify-center text-xs`}>
                  {stats?.protocols > 0 ? '✓' : '4'}
                </span>
                <span className={stats?.protocols > 0 ? 'line-through text-stone-400' : ''}>Create protocols</span>
              </li>
              <li className="flex items-center gap-2">
                <span className={`w-5 h-5 rounded-full bg-stone-300 text-white flex items-center justify-center text-xs`}>5</span>
                <span>Set up answer scoring rules</span>
              </li>
            </ol>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminDashboard;
